// Contratos del módulo "Guía Remedial + Reintento".
// Ver memoria project_remedial_guide_retry_flow para el flujo completo.
import { z } from 'zod';
// Dispara la guía remedial cualquier dictamen bajo el umbral de dominio DEL HITO
// (states `no_evidencia` o `en_desarrollo`; el umbral es 80 solo si el hito no
// trae uno propio).
// Suficiente/profundo NO gatilla — el alumno ya aprobó.
export const REMEDIAL_TRIGGER_STATES = [
    'no_evidencia',
    'en_desarrollo',
];
export function shouldTriggerRemedial(state) {
    return REMEDIAL_TRIGGER_STATES.includes(state);
}
// MIME whitelist del upload del alumno. Cualquier otro tipo se rechaza en
// el pre-signed URL step (no llega ni a Azure Blob).
export const REMEDIAL_UPLOAD_ALLOWED_MIMES = [
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
    'image/jpeg',
    'image/png',
    'image/heic',
    'image/webp',
];
export const REMEDIAL_UPLOAD_MAX_BYTES = 20 * 1024 * 1024; // 20 MB
// Rechazamos uploads triviales (foto negra, PDF vacío) antes de llamar a la IA.
export const REMEDIAL_UPLOAD_MIN_BYTES = 5 * 1024; // 5 KB
// SAS token TTL en minutos. Corto por diseño — el SPA lo pide "just-in-time"
// justo antes del PUT/GET, no lo cachea.
export const REMEDIAL_SAS_TTL_MIN = 10;
// -----------------------------------------------------------------------------
// Zod schemas — request/response de /v1/remedial/*.
// -----------------------------------------------------------------------------
// GET /v1/remedial/tasks/:sessionId
// Devuelve la ronda activa (última) del pilot. Si no existe fila, 404.
export const RemedialTaskResponseSchema = z.object({
    id: z.string().uuid(),
    session_id: z.string().uuid(),
    milestone_id: z.string().nullable(),
    oa_code: z.string(),
    round_number: z.number().int().positive(),
    guide_ready: z.boolean(),
    guide_download_url: z.string().url().nullable(),
    guide_generated_at: z.string().datetime().nullable(),
    upload_ready: z.boolean(),
    uploaded_at: z.string().datetime().nullable(),
    validation_status: z.enum(['pending', 'ok', 'rejected']),
    validation_notes: z.array(z.string()).nullable(),
    validated_at: z.string().datetime().nullable(),
    retry_session_id: z.string().uuid().nullable(),
    retry_created_at: z.string().datetime().nullable(),
    // Link-token del reintento cuando la validación pasó. Permite que el alumno
    // navegue directo a `/entrar/<token>` sin que el docente re-emita 30 links.
    // null cuando no hay retry todavía o cuando el caller no vino por link-token
    // (docente mirando historial no necesita este campo).
    retry_link_token: z.string().nullable(),
});
// POST /v1/remedial/tasks/:sessionId/upload-url
// Alumno pide un pre-signed URL para PUT del archivo. Server valida MIME + tamaño
// declarados, emite SAS write de 10 min, y guarda el blob_ref esperado.
export const RemedialUploadUrlRequestSchema = z.object({
    mime: z.enum([
        'application/pdf',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'image/jpeg',
        'image/png',
        'image/heic',
        'image/webp',
    ]),
    size_bytes: z
        .number()
        .int()
        .positive()
        .max(REMEDIAL_UPLOAD_MAX_BYTES)
        .refine((n) => n >= REMEDIAL_UPLOAD_MIN_BYTES, {
        message: `El archivo debe pesar al menos ${REMEDIAL_UPLOAD_MIN_BYTES} bytes`,
    }),
});
export const RemedialUploadUrlResponseSchema = z.object({
    upload_url: z.string().url(),
    blob_ref: z.string(),
    expires_at: z.string().datetime(),
});
// POST /v1/remedial/tasks/:sessionId/confirm-upload
// Alumno avisa que subió. Server verifica que el blob existe con el size
// declarado, dispara validador IA, y devuelve el nuevo estado.
export const RemedialConfirmUploadRequestSchema = z.object({
    blob_ref: z.string().min(1),
});
//# sourceMappingURL=remedial.js.map